import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  Box,
  Container,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from '@mui/material'
import Footer from 'components/Layout/Footer'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'
import EmployerUserEmailStep from './EmployerUserEmailStep'
import EmployerUserAccountSetupStep from './EmployerUserAccountSetupStep'
import EmployerUserEmailConfirmationStep, { WIZARD_STORAGE_KEY } from './EmployerUserEmailConfirmationStep'
import EmployerUserJobFormStep from './EmployerUserJobFormStep'
import EmployerUserConfirmationStep from './EmployerUserConfirmationStep'

const initialWizardData = {
  email: '',
  firstName: '',
  lastName: '',
  jobTitle: '',
  employerName: '',
  employerId: null,
  password: '',
  userId: null,
  employerUserId: null,
  matchCounts: null,
  jobId: null,
}

const STEP_LABELS = ['Email', 'Account', 'Verify', 'Post Job']

const STEP_CONTENT = [
  {
    headline: 'Post your first job free.',
    value: 'See how many candidates match your role and make your job searchable by active job-seekers.',
  },
  {
    headline: 'Unlimited from day one.',
    value: 'No credits, no caps, no placement fees. Unlimited jobs and candidates.',
  },
  {
    headline: 'Candidates who joined to get hired.',
    value: 'While you verify, active job-seekers are already on the platform waiting to hear from you.',
  },
]

const EmployerRegister = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const theme = useTheme()
  const [searchParams, setSearchParams] = useSearchParams()
  const [step, setStep] = useState(0)
  const [wizardData, setWizardData] = useState(initialWizardData)

  useEffect(() => {
    const isResume = searchParams.get('resume') === 'true'
    if (isResume) {
      try {
        const stored = JSON.parse(localStorage.getItem(WIZARD_STORAGE_KEY))
        if (stored?.employerUserId) {
          setWizardData(stored)
          setStep(3)
          dispatch(
            fetchTrackEvent({
              eventName: 'signup_resumed',
              eventType: ProductAnalyticsEventTypes.ACTION,
              distinctId: stored.email,
              eventData: { resumedFromStep: 3 },
            }),
          )
        }
      } catch { /* ignore parse errors */ }
      setSearchParams({}, { replace: true })
    } else {
      dispatch(
        fetchTrackEvent({
          eventName: 'signup_started',
          eventType: ProductAnalyticsEventTypes.ACTION,
        }),
      )
    }
  }, [])

  const handleNext = () => {
    setStep(prev => {
      if (prev + 1 === 4) {
        localStorage.removeItem(WIZARD_STORAGE_KEY)
      }
      return prev + 1
    })
  }
  const handleBack = () => setStep(prev => prev - 1)

  if (step === 4) {
    return (
      <Container maxWidth='md' sx={{ py: 4 }}>
        <EmployerUserConfirmationStep wizardData={wizardData} />
      </Container>
    )
  }

  if (step === 3) {
    return (
      <>
        <Container maxWidth='md' sx={{ py: 4 }}>
          <EmployerUserJobFormStep
            wizardData={wizardData}
            setWizardData={setWizardData}
            onNext={handleNext}
          />
        </Container>
        <Footer />
      </>
    )
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: theme.palette.background.dark,
          pt: { xs: 5, md: 6 },
          pb: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth='sm'>
          <Stepper
            activeStep={step}
            sx={{
              mb: 4,
              '& .MuiStepLabel-label': {
                color: 'rgba(255,255,255,0.5)',
                '&.Mui-active': { color: theme.palette.common.white, fontWeight: 600 },
                '&.Mui-completed': { color: 'rgba(255,255,255,0.7)' },
              },
              '& .MuiStepIcon-root': {
                color: 'rgba(255,255,255,0.25)',
                '&.Mui-active': { color: theme.palette.primary.light },
                '&.Mui-completed': { color: theme.palette.primary.light },
              },
            }}
          >
            {STEP_LABELS.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Typography
            variant='h4'
            sx={{
              color: theme.palette.common.white,
              fontWeight: 700,
              textAlign: 'center',
              mb: 1,
            }}
          >
            {STEP_CONTENT[step].headline}
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '1.05rem',
              textAlign: 'center',
              lineHeight: 1.6,
            }}
          >
            {STEP_CONTENT[step].value}
          </Typography>
        </Container>
      </Box>

      <Container
        maxWidth='sm'
        sx={{ mt: { xs: -5, md: -6 }, mb: 6, position: 'relative', zIndex: 1 }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.common.white,
            borderRadius: '12px',
            p: { xs: 3, md: 4 },
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}
        >
          {step === 0 && (
            <EmployerUserEmailStep
              wizardData={wizardData}
              setWizardData={setWizardData}
              onNext={handleNext}
              onBack={() => navigate('/employers')}
            />
          )}
          {step === 1 && (
            <EmployerUserAccountSetupStep
              wizardData={wizardData}
              setWizardData={setWizardData}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}
          {step === 2 && (
            <EmployerUserEmailConfirmationStep
              wizardData={wizardData}
              setWizardData={setWizardData}
              onNext={handleNext}
            />
          )}
        </Box>
      </Container>

      <Footer />
    </>
  )
}

export default EmployerRegister
